// src/utils/printThermalInvoice.ts
// Thermal (80mm / 58mm) sales invoice print + Print Settings + one-click print + UPI QR (offline)
// + per-field design (font size / bold / show-hide) from thermalDesign.ts
// - Page width = roll width, height auto-calculated from content (no paper waste)
// - @page margin 0 → no browser date/time/URL header-footer

import { getData } from '@/services/FetchNodeServices';
import { printSalesInvoice, PrintInvoiceData } from './printDocument';
import QRCode from 'qrcode';
import { getThermalDesign, ThermalDesign, ThermalFieldKey } from './thermalDesign';

export type ThermalWidth = number; // roll width in mm
export type InvoicePrintFormat = 'A4' | 'THERMAL_80' | 'THERMAL_58' | 'THERMAL_CUSTOM';
export type QrMode = 'none' | 'upi' | 'image';

/** Used by the format dropdown inside the print preview */
export const PRINT_FORMAT_KEY = 'invoice_print_format';

// ─── Print Settings (saved per computer) ─────────────────────────────────────
export interface PrintSettings {
  format: InvoicePrintFormat;
  thermalWidthMm: number; // used when format = THERMAL_CUSTOM
  showPreview: boolean;
  autoPrintOnSave: boolean;
  footerText: string;
  terms: string[];
  qrMode: QrMode;
  upiId: string;
  upiName: string;
  qrWithAmount: boolean;
  qrLabel: string;
  qrImage: string;
}

const PRINT_SETTINGS_KEY = 'invoice_print_settings';

export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  format: 'A4',
  thermalWidthMm: 80,
  showPreview: true,
  autoPrintOnSave: false,
  footerText: 'Thank you! Visit Again',
  terms: [],
  qrMode: 'none',
  upiId: '',
  upiName: '',
  qrWithAmount: true,
  qrLabel: 'SCAN TO PAY',
  qrImage: '',
};

export function getPrintSettings(): PrintSettings {
  try {
    const raw = localStorage.getItem(PRINT_SETTINGS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      const format: InvoicePrintFormat =
        p.format === 'THERMAL_80' || p.format === 'THERMAL_58' || p.format === 'THERMAL_CUSTOM' || p.format === 'A4'
          ? p.format
          : 'A4';
      const qrMode: QrMode = p.qrMode === 'upi' || p.qrMode === 'image' ? p.qrMode : 'none';
      const str = (v: unknown, d: string) => (typeof v === 'string' ? v : d);
      return {
        format,
        thermalWidthMm: Math.min(Math.max(Number(p.thermalWidthMm) || 80, 40), 120),
        showPreview: p.showPreview !== false,
        autoPrintOnSave: p.autoPrintOnSave === true,
        footerText: str(p.footerText, DEFAULT_PRINT_SETTINGS.footerText),
        terms: Array.isArray(p.terms) ? p.terms.filter((t: unknown) => typeof t === 'string' && t.trim()) : [],
        qrMode,
        upiId: str(p.upiId, ''),
        upiName: str(p.upiName, ''),
        qrWithAmount: p.qrWithAmount !== false,
        qrLabel: str(p.qrLabel, DEFAULT_PRINT_SETTINGS.qrLabel),
        qrImage: str(p.qrImage, ''),
      };
    }
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_PRINT_SETTINGS };
}

export function savePrintSettings(s: PrintSettings): boolean {
  try {
    localStorage.setItem(PRINT_SETTINGS_KEY, JSON.stringify(s));
    localStorage.setItem(PRINT_FORMAT_KEY, s.format);
    return true;
  } catch {
    return false;
  }
}

/** Default format for the preview dropdown */
export function getSavedPrintFormat(): InvoicePrintFormat {
  return getPrintSettings().format;
}

// ─── UPI QR helpers ──────────────────────────────────────────────────────────
export function isValidUpiId(v: string): boolean {
  return /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z0-9.\-]{1,63}$/.test((v || '').trim());
}

export function buildUpiUrl(upiId: string, name: string, amount?: number, note?: string): string {
  const parts = [`pa=${encodeURIComponent(upiId.trim())}`, `pn=${encodeURIComponent(name.trim() || 'Merchant')}`];
  if (amount && amount > 0) parts.push(`am=${amount.toFixed(2)}`);
  parts.push('cu=INR');
  if (note) parts.push(`tn=${encodeURIComponent(note)}`);
  return `upi://pay?${parts.join('&')}`;
}

/** Generate QR as image data URL — works offline (library is bundled in the app) */
export async function makeQrDataUrl(text: string, size = 512): Promise<string> {
  try {
    return await QRCode.toDataURL(text, { margin: 1, width: size, errorCorrectionLevel: 'M' });
  } catch {
    return '';
  }
}

// ─── Company info ─────────────────────────────────────────────────────────────
export interface ThermalOptions {
  width?: ThermalWidth;
  terms?: string[];
  footerText?: string;
}

export interface CompanyInfo {
  name: string;
  address: string;
  gstin: string;
  phone: string;
  email: string;
  logo: string;
}

let companyCache: CompanyInfo | null = null;

export async function getCompanyInfo(): Promise<CompanyInfo> {
  if (companyCache) return companyCache;
  const fallback: CompanyInfo = { name: 'Company Name', address: '', gstin: '', phone: '', email: '', logo: '' };
  try {
    const res: any = await getData('api/v1/company/get');
    const d = res?.data;
    if (!d) return fallback;
    const logo =
      typeof d.logo_url === 'string' && (d.logo_url.startsWith('data:image') || d.logo_url.startsWith('http'))
        ? d.logo_url
        : '';
    companyCache = {
      name: d.name || fallback.name,
      address: d.address || '',
      gstin: d.gstin || '',
      phone: d.phone || '',
      email: d.email || '',
      logo,
    };
    return companyCache;
  } catch {
    return fallback;
  }
}

/** Invoice data company fields first, API as fallback */
export function resolveCompany(data: PrintInvoiceData, api: CompanyInfo): CompanyInfo {
  const d = data as PrintInvoiceData & {
    companyName?: string;
    companyAddress?: string;
    companyGstin?: string;
    companyPhone?: string;
    companyEmail?: string;
  };
  return {
    name: d.companyName || api.name,
    address: d.companyAddress || api.address,
    gstin: d.warehouseGstin || d.companyGstin || api.gstin,
    phone: d.companyPhone || api.phone,
    email: d.companyEmail || api.email,
    logo: api.logo,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function esc(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function inr(n: number | undefined | null): string {
  const v = Number(n) || 0;
  return `&#8377;${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function amountInWords(amount: number): string {
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
    'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const toWords = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return units[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
    if (n < 1000) return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + toWords(n % 100) : '');
    if (n < 100000) return toWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + toWords(n % 1000) : '');
    if (n < 10000000) return toWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + toWords(n % 100000) : '');
    return toWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + toWords(n % 10000000) : '');
  };
  const rupees = Math.floor(Math.abs(amount));
  const paise = Math.round((Math.abs(amount) - rupees) * 100);
  let words = 'Rupees ' + (toWords(rupees) || 'Zero');
  if (paise > 0) words += ' and ' + toWords(paise) + ' Paise';
  return words + ' Only';
}

const row = (label: string, value: string, cls = '') =>
  `<div class="kv ${cls}"><span>${label}</span><span>${value}</span></div>`;

// ─── Thermal HTML builder (used by print AND settings preview) ───────────────
export interface BuildThermalArgs {
  width: ThermalWidth;
  company: CompanyInfo;
  settings: PrintSettings;
  design: ThermalDesign;
  qrDataUrl?: string;
  autoPrint?: boolean;
  terms?: string[];
  footerText?: string;
}

export function buildThermalHtml(data: PrintInvoiceData, a: BuildThermalArgs): string {
  const { width, company: c, settings, design } = a;
  const F = design.fields;
  const sc = width < 70 ? design.scale58 / 100 : 1;
  const sz = (k: ThermalFieldKey) => `${(F[k].size * sc).toFixed(1)}px`;
  const wt = (k: ThermalFieldKey) => (F[k].bold ? 700 : 400);
  const on = (k: ThermalFieldKey) => F[k].show;

  const sidePad = Math.min(design.sideMarginMm, width / 4);
  const content = width - sidePad * 2;
  const qrMM = Math.round(Math.min(design.qrSizeMm * (width < 70 ? 0.85 : 1), content));
  const qtyCol = Math.max(9, Math.round(width * 0.18));
  const amtCol = Math.max(13, Math.round(width * 0.24));
  const footerText = a.footerText ?? settings.footerText;
  const termsList = a.terms ?? settings.terms;

  // ── Items ──
  const itemsHtml = data.items
    .map((it) => {
      const sub: string[] = [];
      if (on('itemDetails')) {
        sub.push(`${inr(it.rate)}${it.taxRate ? ` (GST ${esc(it.taxRate)}%)` : ''}`);
        if (it.hsn) sub.push(`HSN: ${esc(it.hsn)}`);
        if (it.discount > 0) sub.push(`Disc: ${esc(it.discount)}%`);
      }
      return `
      <div class="item">
        <div class="i-name">${esc(it.name)}</div>
        <div class="i-qty">${esc(it.qty)} ${esc(it.unit || '')}</div>
        <div class="i-amt">${inr(it.total)}</div>
        ${sub.length ? `<div class="i-sub">${sub.join('<br/>')}</div>` : ''}
      </div>`;
    })
    .join('');

  // ── Totals ──
  const totals: string[] = [];
  totals.push(row('Item Total:', inr(data.subtotal)));
  if (data.totalDiscount > 0) totals.push(row('Discount:', `- ${inr(data.totalDiscount)}`));
  totals.push(row('Taxable Amount:', inr(data.taxableAmount)));
  if (data.isSameState) {
    totals.push(row('CGST:', inr(data.cgst)));
    totals.push(row('SGST:', inr(data.sgst)));
  } else {
    totals.push(row('IGST:', inr(data.igst)));
  }
  if (data.roundOff) totals.push(row('Round Off:', `${data.roundOff > 0 ? '+' : '-'} ${inr(Math.abs(data.roundOff))}`));
  totals.push(row('Grand Total:', inr(data.grandTotal), 'grand'));
  if (data.paidAmount !== undefined) totals.push(row('Paid Amount:', inr(data.paidAmount)));
  if (data.balanceDue && data.balanceDue > 0) totals.push(row('Balance Due:', inr(data.balanceDue)));

  const paymentModes =
    on('paymentDetails') && data.paymentModes && data.paymentModes.length > 0
      ? `<div class="dash"></div><div class="s-pay"><div class="sec-title">Payment Details</div>${data.paymentModes
          .map((pm) => row(`${esc(pm.mode)}:`, inr(pm.amount)))
          .join('')}</div>`
      : '';

  // ── QR ──
  let qrHtml = '';
  if (a.qrDataUrl) {
    const label = on('qrLabel') && settings.qrLabel ? `<div class="qr-label">${esc(settings.qrLabel)}</div>` : '';
    const upiLine =
      on('qrLabel') && settings.qrMode === 'upi' && settings.upiId
        ? `<div class="qr-upi">UPI: ${esc(settings.upiId)}</div>`
        : '';
    qrHtml = `<div class="dash"></div><div class="qr-box"><img class="qr-img" src="${esc(a.qrDataUrl)}" alt="" />${label}${upiLine}</div>`;
  }

  const terms =
    on('terms') && termsList && termsList.length
      ? `<div class="dash"></div><div class="s-terms"><div class="sec-title">T &amp; C</div>${termsList
          .map((t) => `<div>${esc(t)}</div>`)
          .join('')}</div>`
      : '';

  const footerParts: string[] = [];
  if (on('footer') && footerText) footerParts.push(`<div class="ft-main">${esc(footerText)}</div>`);
  if (on('generatedLine')) footerParts.push(`<div class="ft-gen">This is a computer generated invoice.</div>`);
  const footer = footerParts.length ? `<div class="dash"></div><div class="footer">${footerParts.join('')}</div>` : '';

  const header: string[] = [];
  if (on('logo') && c.logo) header.push(`<img class="logo" src="${esc(c.logo)}" alt="" />`);
  if (on('companyName')) header.push(`<div class="c-name">${esc(c.name)}</div>`);
  if (on('companyAddress') && c.address) header.push(`<div class="c-addr">${esc(c.address)}</div>`);
  if (on('companyContact')) {
    if (c.gstin) header.push(`<div class="c-contact">GSTIN: ${esc(c.gstin)}</div>`);
    if (c.phone) header.push(`<div class="c-contact">Ph: ${esc(c.phone)}</div>`);
    if (c.email) header.push(`<div class="c-contact">${esc(c.email)}</div>`);
  }

  const script = a.autoPrint
    ? `<script>
(function(){
  function go(){
    setTimeout(function(){
      var px = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      var mm = Math.ceil(px * 25.4 / 96) + 3;
      var st = document.createElement('style');
      st.textContent = '@page{size:${width}mm ' + mm + 'mm;margin:0}';
      document.head.appendChild(st);
      setTimeout(function(){ window.focus(); window.print(); }, 250);
    }, 300);
  }
  window.onafterprint = function(){ setTimeout(function(){ window.close(); }, 300); };
  if (document.readyState === 'complete') go(); else window.onload = go;
})();
<\/script>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<title>&nbsp;</title>
<style>
  @page { size: ${width}mm 297mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: ${width}mm; background: #fff; }
  body { padding: 3mm ${sidePad}mm 4mm; font-family: Arial, Helvetica, sans-serif; font-size: ${sz('billInfo')}; color: #000;
         line-height: ${design.lineHeight}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .center { text-align: center; }
  .logo { display: block; margin: 0 auto 1.5mm; max-height: ${design.logoHeightMm}mm; max-width: 40mm; object-fit: contain; }
  .c-name { font-size: ${sz('companyName')}; font-weight: ${wt('companyName')}; }
  .c-addr { font-size: ${sz('companyAddress')}; font-weight: ${wt('companyAddress')}; }
  .c-contact { font-size: ${sz('companyContact')}; font-weight: ${wt('companyContact')}; }
  .title { font-size: ${sz('title')}; font-weight: ${wt('title')}; letter-spacing: 1px; margin: 1mm 0; }
  .dash { border-top: 1px dashed #000; margin: 1.5mm 0; }
  .kv { display: flex; justify-content: space-between; gap: 2mm; }
  .kv span:first-child { font-weight: 700; white-space: nowrap; }
  .kv span:last-child { text-align: right; word-break: break-word; }
  .sec-title { font-weight: 700; margin: 0.5mm 0; }

  .s-bill { font-size: ${sz('billInfo')}; }
  .s-bill .kv span:last-child { font-weight: ${wt('billInfo')}; }
  .s-cust { font-size: ${sz('customer')}; }
  .s-cust .kv span:last-child, .s-cust .addr { font-weight: ${wt('customer')}; }

  .items-head, .item { display: grid; grid-template-columns: 1fr ${qtyCol}mm ${amtCol}mm; column-gap: 1mm; }
  .items-head { font-size: ${sz('itemQtyAmount')}; font-weight: 700; padding: 0.5mm 0; }
  .items-head div:nth-child(2), .i-qty { text-align: center; }
  .items-head div:nth-child(3), .i-amt { text-align: right; }
  .item { padding: 1mm 0; border-bottom: 1px solid #ddd; }
  .item:last-child { border-bottom: none; }
  .i-name { font-size: ${sz('itemName')}; font-weight: ${wt('itemName')}; word-break: break-word; }
  .i-qty, .i-amt { font-size: ${sz('itemQtyAmount')}; font-weight: ${wt('itemQtyAmount')}; }
  .i-sub { grid-column: 1 / 2; font-size: ${sz('itemDetails')}; font-weight: ${wt('itemDetails')}; color: #222; }

  .s-tot { font-size: ${sz('totals')}; }
  .s-tot .kv span:last-child { font-weight: ${wt('totals')}; }
  .s-tot .kv.grand, .s-tot .kv.grand span { font-size: ${sz('grandTotal')}; font-weight: ${wt('grandTotal')}; }
  .s-tot .kv.grand { margin-top: 1mm; }

  .s-pay { font-size: ${sz('paymentDetails')}; }
  .s-pay .kv span:last-child { font-weight: ${wt('paymentDetails')}; }
  .s-words { font-size: ${sz('amountWords')}; }
  .s-words .words { font-weight: ${wt('amountWords')}; }

  .qr-box { text-align: center; margin: 1.5mm 0; }
  .qr-img { display: block; margin: 0 auto; width: ${qrMM}mm; height: ${qrMM}mm; object-fit: contain; }
  .qr-label { display: inline-block; margin-top: 1.5mm; padding: 0.8mm 4mm; border: 1.5px solid #000; border-radius: 3mm;
              font-size: ${sz('qrLabel')}; font-weight: ${wt('qrLabel')}; letter-spacing: 0.5px; }
  .qr-upi { margin-top: 1mm; font-size: ${sz('qrLabel')}; }

  .s-terms { font-size: ${sz('terms')}; font-weight: ${wt('terms')}; }
  .footer { text-align: center; }
  .ft-main { font-size: ${sz('footer')}; font-weight: ${wt('footer')}; }
  .ft-gen { font-size: ${sz('generatedLine')}; font-weight: ${wt('generatedLine')}; }
  @media screen { html { background: #e5e7eb; } body { margin: 10px auto; box-shadow: 0 0 0 1px #9ca3af; } }
</style>
</head>
<body>
  <div class="center">
    ${header.join('\n    ')}
    ${header.length ? '<div class="dash"></div>' : ''}
    ${on('title') && design.titleText ? `<div class="title">${esc(design.titleText)}</div>` : ''}
  </div>

  <div class="s-bill">
    ${row('Bill No.:', esc(data.invoiceNo))}
    ${row('Date:', esc(data.date))}
    ${data.paymentMode ? row('Payment Mode:', esc(data.paymentMode)) : ''}
    ${data.paymentStatus ? row('Payment Status:', esc(data.paymentStatus)) : ''}
    ${data.warehouseName ? row('Branch:', esc(data.warehouseName)) : ''}
  </div>

  ${
    on('customer')
      ? `<div class="dash"></div>
  <div class="s-cust">
    <div class="sec-title">Billing To</div>
    ${row('Name:', esc(data.customerName))}
    ${data.customerGstin ? row('GSTIN:', esc(data.customerGstin)) : ''}
    ${data.billingAddress ? `<div class="addr">${esc(data.billingAddress)}</div>` : ''}
  </div>`
      : ''
  }

  <div class="dash"></div>
  <div class="items-head"><div>Items</div><div>Qty</div><div>Amount</div></div>
  <div class="dash" style="margin-top:0"></div>
  ${itemsHtml}
  <div class="dash"></div>

  <div class="s-tot">${totals.join('')}</div>

  ${paymentModes}

  ${
    on('amountWords')
      ? `<div class="dash"></div><div class="s-words"><div class="sec-title">Amount in Words</div><div class="words">${esc(
          amountInWords(data.grandTotal),
        )}</div></div>`
      : ''
  }

  ${qrHtml}

  ${data.notes ? `<div class="dash"></div><div class="s-bill"><b>Notes:</b> ${esc(data.notes)}</div>` : ''}
  ${terms}
  ${footer}
${script}
</body>
</html>`;
}

// ─── Thermal print ───────────────────────────────────────────────────────────
export async function printThermalInvoice(data: PrintInvoiceData, opts: ThermalOptions = {}): Promise<void> {
  const settings = getPrintSettings();
  const design = getThermalDesign();
  const width: ThermalWidth = Math.min(Math.max(Number(opts.width) || settings.thermalWidthMm || 80, 40), 120);

  // Open window first (inside click) so popup blocker doesn't block it
  const win = window.open('', '_blank');
  if (!win) {
    alert('Popup blocked. Please allow popups for this site to print.');
    return;
  }
  win.document.write('<p style="font-family:Arial;padding:16px">Preparing bill...</p>');

  const company = resolveCompany(data, await getCompanyInfo());

  let qrDataUrl = '';
  if (settings.qrMode === 'upi' && isValidUpiId(settings.upiId)) {
    const amt = data.balanceDue && data.balanceDue > 0 ? data.balanceDue : data.grandTotal;
    qrDataUrl = await makeQrDataUrl(
      buildUpiUrl(settings.upiId, settings.upiName || company.name, settings.qrWithAmount ? amt : undefined, `Bill ${data.invoiceNo}`),
    );
  } else if (settings.qrMode === 'image' && settings.qrImage) {
    qrDataUrl = settings.qrImage;
  }

  const html = buildThermalHtml(data, {
    width,
    company,
    settings,
    design,
    qrDataUrl,
    autoPrint: true,
    terms: opts.terms,
    footerText: opts.footerText,
  });

  win.document.open();
  win.document.write(html);
  win.document.close();
}

/** Print invoice in a given format */
export async function printInvoiceByFormat(data: PrintInvoiceData, format: InvoicePrintFormat): Promise<void> {
  if (format === 'THERMAL_80') return printThermalInvoice(data, { width: 80 });
  if (format === 'THERMAL_58') return printThermalInvoice(data, { width: 58 });
  if (format === 'THERMAL_CUSTOM') return printThermalInvoice(data, { width: getPrintSettings().thermalWidthMm });
  return printSalesInvoice(data);
}

/** One-click print: preview ON → open preview; OFF → print directly in saved format */
export function quickPrintInvoice(data: PrintInvoiceData, openPreview: () => void): void {
  const s = getPrintSettings();
  if (s.showPreview) {
    openPreview();
    return;
  }
  void printInvoiceByFormat(data, s.format);
}
